// Recent-sizes persistence: localStorage for guests, server for authenticated users.

const STORAGE_KEY = 'designhub:recentSizes';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const MAX_RECENTS = 5;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function isAuthed() {
  try { return Boolean(localStorage.getItem(AUTH_TOKEN_KEY)); } catch { return false; }
}

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
  } catch { return []; }
}

function writeLocal(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
}

function clearLocal() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

function isValidEntry(e) {
  return e && typeof e === 'object'
    && Number.isFinite(e.width) && Number.isFinite(e.height)
    && e.width > 0 && e.height > 0
    && typeof e.name === 'string';
}

function dedupAndCap(list) {
  const seen = new Set();
  const out = [];
  for (const e of list) {
    const key = `${e.width}x${e.height}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
    if (out.length >= MAX_RECENTS) break;
  }
  return out;
}

async function fetchServer() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return [];
  try {
    const res = await fetch(`${API_URL}/api/auth/preferences/recent-sizes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data?.recent_sizes) ? data.recent_sizes : [];
    return list.filter(isValidEntry);
  } catch { return []; }
}

async function putServer(list) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return;
  try {
    await fetch(`${API_URL}/api/auth/preferences/recent-sizes`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recent_sizes: list }),
    });
  } catch { /* offline / 500 — guest list still works */ }
}

export async function loadRecentSizes() {
  if (!isAuthed()) return readLocal();
  const local = readLocal();
  const server = await fetchServer();
  if (local.length === 0) return server;
  // Merge once on first authed load: server wins on duplicate dimensions, then prune local.
  const merged = dedupAndCap([...server, ...local]);
  if (local.length > 0) {
    clearLocal();
    if (merged.length !== server.length) await putServer(merged);
  }
  return merged;
}

export async function pushRecentSize(entry) {
  if (!isValidEntry(entry)) return;
  const stamped = { ...entry, ts: Date.now() };
  if (isAuthed()) {
    const current = await fetchServer();
    const next = dedupAndCap([stamped, ...current]);
    await putServer(next);
    return next;
  }
  const next = dedupAndCap([stamped, ...readLocal()]);
  writeLocal(next);
  return next;
}
